import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import DatePicker from "@/components/DatePicker";
import OdemeSozuDialog from "@/components/OdemeSozuDialog";
import BorcluGorusmeleri from "@/components/BorcluGorusmeleri";
import HacizEvrakSection from "@/components/HacizEvrakSection";
import BorclularSection from "@/components/BorclularSection";
import BelgeTeminatModal from "@/components/BelgeTeminatModal";
import TakipXmlSection from "@/components/TakipXmlSection";
import { api } from "@/lib/api";
import { fmt, fmtDate } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { ArrowLeft, Plus, Trash2, Wallet, FileText, Gavel, FileDown } from "lucide-react";

export default function IcraDosya() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const canHaciz = isAdmin || user?.role === "katip" || user?.role === "avukat";
  const canOdemeSozu = ["admin", "katip", "avukat"].includes(user?.role);
  const canEditIl = (user?.email || "").trim().toLowerCase() === "karakoctekhukuk@gmail.com";
  const [rec, setRec] = useState(null);
  const [showPromise, setShowPromise] = useState(false);
  const [editSoz, setEditSoz] = useState(null);
  const [tf, setTf] = useState({ tarih: new Date().toISOString().slice(0, 10), tutar: "", tur: "dosya" });
  const [di, setDi] = useState({ icra_dairesi: "", dosya_no: "", tc_no: "" });
  const [belgeBusy, setBelgeBusy] = useState(null);
  const [showTeminat, setShowTeminat] = useState(false);
  const [ilDisi, setIlDisi] = useState(null);
  const [vade, setVade] = useState("");
  const [ilVal, setIlVal] = useState("");

  const indirBlob = async (key, path, params) => {
    setBelgeBusy(key);
    try {
      const res = await api.get(path, { params, responseType: "blob" });
      const cd = res.headers["content-disposition"] || "";
      const m = cd.match(/filename\*=UTF-8''([^;]+)/);
      const fname = m ? decodeURIComponent(m[1]) : `belge_${key}`;
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = fname;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Belge indirildi");
      setShowTeminat(false);
    } catch (err) {
      let msg = "Belge oluşturulamadı";
      try { msg = JSON.parse(await err.response.data.text()).detail || msg; } catch (e) { /* ignore */ }
      toast.error(msg);
    } finally { setBelgeBusy(null); }
  };

  const load = useCallback(() => {
    api.get(`/records/${id}`).then((res) => setRec(res.data)).catch(() => toast.error("Dosya yüklenemedi"));
    api.get(`/records/${id}/il-disi-masraf`).then((res) => setIlDisi(res.data)).catch(() => setIlDisi(null));
  }, [id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => { if (rec) { setDi({ icra_dairesi: rec.icra_dairesi || "", dosya_no: rec.dosya_no || "", tc_no: rec.tc_no || "" }); setVade(((rec.senet?.vade_tarihi || rec.vade_tarihi) || "").slice(0, 10)); setIlVal(rec.il || ""); } }, [rec]);

  const addTahsilat = async (e) => {
    e.preventDefault();
    if (!tf.tarih || !(Number(tf.tutar) > 0)) { toast.error("Tarih ve pozitif tutar girin"); return; }
    try {
      await api.post(`/records/${id}/tahsilat`, { tarih: tf.tarih, tutar: Number(tf.tutar), tur: tf.tur });
      toast.success("Tahsilat eklendi");
      setTf({ tarih: new Date().toISOString().slice(0, 10), tutar: "", tur: tf.tur });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Tahsilat eklenemedi"); }
  };

  const delTahsilat = async (index) => {
    try { await api.delete(`/records/${id}/tahsilat/${index}`); toast.success("Silindi"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Silinemedi"); }
  };

  const addPromise = async (payload) => {
    try {
      const arr = Array.isArray(payload) ? payload : [payload];
      for (const p of arr) await api.post(`/records/${id}/odeme-sozu`, p);
      toast.success(arr.length > 1 ? `${arr.length} taksitli ödeme sözü eklendi` : "Ödeme sözü eklendi");
      setShowPromise(false); load();
    } catch (e) { toast.error("Eklenemedi"); }
  };

  const delPromise = async (index) => {
    try { await api.delete(`/records/${id}/odeme-sozu/${index}`); toast.success("Silindi"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Silinemedi"); }
  };

  const saveEditPromise = async (payload) => {
    try {
      await api.put(`/records/${id}/odeme-sozu/${editSoz._index}`, payload);
      toast.success("Ödeme sözü güncellendi"); setEditSoz(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Güncellenemedi"); }
  };

  const addGorusme = async (payload) => {
    try { await api.post(`/records/${id}/gorusme`, payload); toast.success("Görüşme eklendi"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Eklenemedi"); }
  };
  const delGorusme = async (gid) => {
    try { await api.delete(`/records/${id}/gorusme/${gid}`); toast.success("Silindi"); load(); }
    catch (e) { toast.error("Silinemedi"); }
  };

  const saveDosya = async () => {
    try { await api.put(`/records/${id}/dosya-bilgi`, di); toast.success("Dosya bilgileri kaydedildi"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Kaydedilemedi"); }
  };

  const saveVade = async () => {
    try { await api.put(`/records/${id}/senet-vade`, { vade_tarihi: vade || null }); toast.success("Senet vade tarihi kaydedildi (belgeye işlenecek)"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Kaydedilemedi"); }
  };

  const saveIl = async () => {
    try { await api.put(`/records/${id}/il`, { il: ilVal || "" }); toast.success("İl güncellendi"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "İl güncellenemedi"); }
  };

  if (!rec) return <Layout><div className="py-24 text-center text-[#8C8377]">Yükleniyor...</div></Layout>;

  const tahsilatlar = rec.tahsilatlar || [];
  const promises = rec.odeme_sozleri || [];

  return (
    <Layout>
      <button onClick={() => navigate(-1)} data-testid="icra-back" className="flex items-center gap-1.5 text-sm text-[#8C8377] hover:text-[#26231E] mb-4 transition-colors duration-150">
        <ArrowLeft className="w-4 h-4" /> Geri
      </button>

      <div className="mb-6">
        <div className="kicker mb-1">İCRA DOSYA FÖYÜ</div>
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="font-heading text-3xl font-bold tracking-tight" data-testid="icra-dosya-musteri">{rec.musteri}</h1>
          {rec.durum === "İNFAZ" ? (
            <span data-testid="icra-infaz-badge" className="inline-flex items-center gap-1 px-2.5 py-1 rounded-sm text-xs font-semibold bg-[#2F6E4E]/10 text-[#2F6E4E]"><Gavel className="w-3.5 h-3.5" /> İNFAZ</span>
          ) : (
            <span data-testid="icra-durum-badge" className="inline-flex items-center gap-1 px-2.5 py-1 rounded-sm text-xs font-semibold bg-[#B8933F]/10 text-[#B8933F]"><Gavel className="w-3.5 h-3.5" /> Devam Eden</span>
          )}
        </div>
        <div className="text-sm text-[#8C8377] mt-1">TC: {rec.tc_no || "-"} · Dosya Tarihi: {fmtDate(rec.tarih)}{rec.il ? ` · İl: ${rec.il}` : ""} {rec.aciklama ? `· ${rec.aciklama}` : ""}</div>
        {canEditIl && (
          <div className="mt-2 flex items-center gap-2" data-testid="icra-il-edit-box">
            <label className="text-xs text-[#8C8377]">İl / Şehir:</label>
            <input value={ilVal} onChange={(e) => setIlVal(e.target.value)} data-testid="icra-il-input" placeholder="örn. İstanbul" className="px-3 py-1.5 border border-[#E7DECC] rounded-sm text-sm w-48 focus:outline-none focus:border-[#B8933F]" />
            <button onClick={saveIl} data-testid="icra-il-kaydet" className="px-3 py-1.5 bg-[#B8933F] hover:bg-[#9C7A33] text-white rounded-sm text-sm font-medium transition-colors duration-150">İl Kaydet</button>
          </div>
        )}
      </div>

      <div className="bg-white border border-[#E7DECC] rounded-sm p-4 mb-6" data-testid="icra-dosya-bilgi">
        <div className="kicker mb-3">İCRA DOSYA BİLGİLERİ (UYAP)</div>
        {canHaciz ? (
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="text-xs text-[#8C8377] block mb-1">Dosya No</label>
              <input value={di.dosya_no} onChange={(e) => setDi({ ...di, dosya_no: e.target.value })} data-testid="dosya-no-input" placeholder="örn. 2026/1234" className="px-3 py-2 border border-[#E7DECC] rounded-sm text-sm focus:outline-none focus:border-[#B8933F]" />
            </div>
            <div>
              <label className="text-xs text-[#8C8377] block mb-1">İcra Dairesi</label>
              <input value={di.icra_dairesi} onChange={(e) => setDi({ ...di, icra_dairesi: e.target.value })} data-testid="icra-dairesi-input" placeholder="örn. İstanbul 5. İcra Dairesi" className="px-3 py-2 border border-[#E7DECC] rounded-sm text-sm w-72 focus:outline-none focus:border-[#B8933F]" />
            </div>
            <div>
              <label className="text-xs text-[#8C8377] block mb-1">TC Kimlik No</label>
              <input value={di.tc_no} onChange={(e) => setDi({ ...di, tc_no: e.target.value })} data-testid="tc-no-input" placeholder="11 haneli TC" className="px-3 py-2 border border-[#E7DECC] rounded-sm text-sm w-40 focus:outline-none focus:border-[#B8933F]" />
            </div>
            <button onClick={saveDosya} data-testid="dosya-bilgi-kaydet" className="px-4 py-2 bg-[#B8933F] hover:bg-[#9C7A33] text-white rounded-sm text-sm font-medium transition-colors duration-150">Kaydet</button>
            <div className="w-px h-9 bg-[#E7DECC] mx-1 self-center hidden md:block" />
            <div>
              <label className="text-xs text-[#8C8377] block mb-1">Senet Vade Tarihi (belgeye gider)</label>
              <input type="date" value={vade} onChange={(e) => setVade(e.target.value)} data-testid="senet-vade-input" className="px-3 py-2 border border-[#E7DECC] rounded-sm text-sm focus:outline-none focus:border-[#B8933F]" />
            </div>
            <button onClick={saveVade} data-testid="senet-vade-kaydet" className="px-4 py-2 bg-[#26231E] hover:bg-[#333] text-white rounded-sm text-sm font-medium transition-colors duration-150">Vade Kaydet</button>
          </div>
        ) : (
          <div className="flex flex-wrap gap-6 text-sm">
            <div><span className="text-[#8C8377]">Dosya No: </span><b>{rec.dosya_no || "-"}</b></div>
            <div><span className="text-[#8C8377]">İcra Dairesi: </span><b>{rec.icra_dairesi || "-"}</b></div>
            <div><span className="text-[#8C8377]">Senet Vade: </span><b>{fmtDate(rec.senet?.vade_tarihi || rec.vade_tarihi) || "-"}</b></div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
        <Stat label="Alış Fiyatı (AF)" value={fmt(rec.alis_fiyati)} testId="stat-af" />
        <Stat label="Satış Fiyatı (SF)" value={fmt(rec.satis_fiyati)} testId="stat-sf" />
        <Stat label="İcra Takibine Giren Tutar" value={fmt(rec.asil_alacak)} testId="stat-asil-alacak" />
        <Stat label="Tahsilat" value={fmt(rec.tahsilat)} color="#2F6E4E" testId="stat-toplam-tahsilat" />
        <Stat label="Faiz Tahsilatı" value={fmt(rec.faiz_tahsilat)} color="#7C5E9B" testId="stat-faiz-tahsilat" />
        <Stat label="Kalan" value={fmt(rec.kalan)} color={rec.kalan > 0 ? "#A63A3A" : "#2F6E4E"} testId="stat-kalan" />
        {isAdmin && (
          <>
            <Stat label="Brüt Risk" value={fmt(rec.brut_risk)} color={rec.brut_risk > 0 ? "#A63A3A" : "#2F6E4E"} testId="stat-brut-risk" />
            <Stat label="Net Risk / Kâr" value={fmt(rec.net_risk)} color={rec.net_risk < 0 ? "#2F6E4E" : "#A63A3A"} testId="stat-net-risk" />
          </>
        )}
        <Stat label="İl Dışı Resmi Masraf" value={fmt(ilDisi?.toplam_resmi_dahil_harc || 0)} color="#B8933F" testId="stat-il-disi-resmi-masraf" />
      </div>

      {ilDisi && (ilDisi.kalemler?.length > 0 || ilDisi.resmi_masraf_total > 0) && (
        <div className="bg-white border border-[#E7DECC] rounded-sm mb-6" data-testid="icra-il-disi-masraf">
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#E7DECC]">
            <div className="font-heading font-bold text-sm text-[#B8933F]">İl Dışı Harç & Masraf (Resmi)</div>
            <div className="text-sm">Resmi Toplam: <b className="text-[#B8933F] tabular-nums">{fmt(ilDisi.toplam_resmi_dahil_harc)}</b>
              {ilDisi.ihtiyati_haciz_harci > 0 && <span className="ml-3 text-[#8C8377]">İH Başvuru: <b className="text-[#5E5CE6] tabular-nums">{fmt(ilDisi.ihtiyati_haciz_harci)}</b></span>}
              {ilDisi.gayri_resmi_masraf > 0 && <span className="ml-3 text-[#8C8377]">Gayri Resmi: <b className="text-[#B8933F] tabular-nums">{fmt(ilDisi.gayri_resmi_masraf)}</b></span>}
            </div>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-[#F3ECDD] border-b border-[#E7DECC]"><tr>
              {["Borçlu", "Dosya No", "Takip Açılış", "Toplam Masraf", "Gayri Resmi", "Resmi Masraf Total", "İH Başvuru"].map((h, i) => <th key={h} className={`px-3 py-2 kicker ${i < 2 ? "text-left" : "text-right"}`}>{h}</th>)}
            </tr></thead>
            <tbody>
              {(ilDisi.kalemler || []).map((k) => (
                <tr key={k.id} className="border-b border-[#EFE7D6]" data-testid={`il-disi-kalem-${k.id}`}>
                  <td className="px-3 py-2">{k.borclu}</td>
                  <td className="px-3 py-2">{k.dosya_no || "-"}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmt(k.takip_acilis_masrafi)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmt(k.toplam_masraf)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-[#B8933F]">{fmt(k.gayri_resmi_masraf)}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-semibold text-[#B8933F]">{fmt(k.resmi_masraf_total)}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-semibold text-[#5E5CE6]">{fmt(k.harc_i)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Tahsilatlar */}
      <div className="bg-white border border-[#E7DECC] rounded-sm mb-6" data-testid="icra-tahsilatlar">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-[#E7DECC]">
          <Wallet className="w-4 h-4 text-[#B8933F]" />
          <div className="kicker">TAHSİLATLAR (DOSYA / FAİZ)</div>
        </div>
        <form onSubmit={addTahsilat} className="flex flex-wrap items-end gap-3 p-4 border-b border-[#E7DECC] bg-[#F3ECDD]" style={isAdmin ? undefined : { display: "none" }}>
          <div className="w-[180px]">
            <label className="kicker block mb-1.5">TARİH</label>
            <DatePicker value={tf.tarih} onChange={(v) => setTf((s) => ({ ...s, tarih: v }))} testId="icra-tahsilat-tarih" />
          </div>
          <div className="w-[160px]">
            <label className="kicker block mb-1.5">TÜR</label>
            <select
              value={tf.tur}
              onChange={(e) => setTf((s) => ({ ...s, tur: e.target.value }))}
              data-testid="icra-tahsilat-tur"
              className="w-full px-3 py-2 border border-[#E7DECC] rounded-sm text-sm focus:outline-none focus:border-[#B8933F] bg-white"
            >
              <option value="dosya">Dosya Tahsilatı</option>
              <option value="faiz">Faiz Tahsilatı</option>
            </select>
          </div>
          <div className="w-[160px]">
            <label className="kicker block mb-1.5">TUTAR</label>
            <input type="number" min="0" step="0.01" value={tf.tutar} onChange={(e) => setTf((s) => ({ ...s, tutar: e.target.value }))} data-testid="icra-tahsilat-tutar" className="w-full px-3 py-2 border border-[#E7DECC] rounded-sm tabular-nums focus:outline-none focus:border-[#B8933F]" placeholder="0" />
          </div>
          <button type="submit" data-testid="icra-tahsilat-ekle" className="flex items-center gap-1.5 px-4 py-2 bg-[#B8933F] hover:bg-[#9C7A33] text-white rounded-sm text-sm font-medium transition-colors duration-150"><Plus className="w-4 h-4" /> Ekle</button>
        </form>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#E7DECC] bg-[#F3ECDD]">
              <th className="px-4 py-2.5 kicker text-left">Tarih</th>
              <th className="px-4 py-2.5 kicker text-left">Tür</th>
              <th className="px-4 py-2.5 kicker text-right">Tutar</th>
              <th className="px-4 py-2.5 kicker text-right">İşlem</th>
            </tr>
          </thead>
          <tbody>
            {tahsilatlar.length === 0 ? (
              <tr><td colSpan={4} className="px-4 py-6 text-center text-[#8C8377]">Henüz tahsilat yok.</td></tr>
            ) : tahsilatlar.map((t, idx) => (
              <tr key={idx} className={`border-b border-[#E7DECC] ${idx % 2 ? "bg-[#F3ECDD]/60" : ""}`} data-testid={`icra-tahsilat-row-${idx}`}>
                <td className="px-4 py-2 tabular-nums">{fmtDate(t.tarih)}</td>
                <td className="px-4 py-2">
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-sm ${t.tur === "faiz" ? "bg-[#7C5E9B]/10 text-[#7C5E9B]" : "bg-[#B8933F]/10 text-[#B8933F]"}`}>
                    {t.tur === "faiz" ? "Faiz" : "Dosya"}
                  </span>
                </td>
                <td className={`px-4 py-2 text-right tabular-nums ${t.tur === "faiz" ? "text-[#7C5E9B]" : "text-[#2F6E4E]"}`}>{fmt(t.tutar)}</td>
                <td className="px-4 py-2 text-right">
                  {isAdmin && (
                    <button onClick={() => delTahsilat(idx)} data-testid={`icra-tahsilat-del-${idx}`} className="p-1.5 text-[#8C8377] hover:text-[#A63A3A] rounded-sm transition-colors duration-150"><Trash2 className="w-4 h-4" /></button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Ödeme Sözleri / Anlaşmalar */}
      <div className="bg-white border border-[#E7DECC] rounded-sm" data-testid="icra-sozler">
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#E7DECC]">
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4 text-[#7C5E9B]" />
            <div className="kicker">ÖDEME SÖZLERİ / ANLAŞMALAR</div>
          </div>
          <button onClick={() => setShowPromise(true)} data-testid="icra-sozu-ekle" style={canOdemeSozu ? undefined : { display: "none" }} className="flex items-center gap-1.5 px-3 py-1.5 bg-[#B8933F] hover:bg-[#9C7A33] text-white rounded-sm text-sm font-medium transition-colors duration-150"><Plus className="w-4 h-4" /> Söz Ekle</button>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#E7DECC] bg-[#F3ECDD]">
              <th className="px-4 py-2.5 kicker text-left">Görüşme</th>
              <th className="px-4 py-2.5 kicker text-left">Temsilci</th>
              <th className="px-4 py-2.5 kicker text-left">Mahiyet</th>
              <th className="px-4 py-2.5 kicker text-left">Taahhüt Tarihi</th>
              <th className="px-4 py-2.5 kicker text-right">Taahhüt Tutarı</th>
              <th className="px-4 py-2.5 kicker text-left">Not</th>
              <th className="px-4 py-2.5 kicker text-right">İşlem</th>
            </tr>
          </thead>
          <tbody>
            {promises.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-6 text-center text-[#8C8377]">Henüz ödeme sözü / anlaşma yok.</td></tr>
            ) : promises.map((p, idx) => (
              <tr key={idx} className={`border-b border-[#E7DECC] ${idx % 2 ? "bg-[#F3ECDD]/60" : ""}`} data-testid={`icra-sozu-row-${idx}`}>
                <td className="px-4 py-2 tabular-nums whitespace-nowrap">{fmtDate(p.tarih)}</td>
                <td className="px-4 py-2 whitespace-nowrap">{p.temsilci || "-"}</td>
                <td className="px-4 py-2 whitespace-nowrap">{p.mahiyet ? <span className="px-2 py-0.5 rounded-sm text-xs font-semibold bg-[#B8933F]/10 text-[#B8933F]">{p.mahiyet}</span> : "-"}</td>
                <td className="px-4 py-2 tabular-nums whitespace-nowrap">{p.taahhut_tarihi ? fmtDate(p.taahhut_tarihi) : "-"}</td>
                <td className="px-4 py-2 text-right tabular-nums whitespace-nowrap">{p.taahhut_tutari ? fmt(p.taahhut_tutari) : "-"}</td>
                <td className="px-4 py-2 text-[#8C8377]">{p.aciklama || "-"}</td>
                <td className="px-4 py-2 text-right whitespace-nowrap">
                  {canOdemeSozu && ["avukat", "katip"].includes(p.ekleyen_rol) && (
                    <button onClick={() => setEditSoz({ ...p, _index: idx })} data-testid={`icra-sozu-edit-${idx}`} className="px-2 py-1 mr-1 text-xs font-semibold text-[#B8933F] hover:bg-[#B8933F]/10 rounded-sm transition-colors duration-150">Düzenle</button>
                  )}
                  {isAdmin && (
                    <button onClick={() => delPromise(idx)} data-testid={`icra-sozu-del-${idx}`} className="p-1.5 text-[#8C8377] hover:text-[#A63A3A] rounded-sm transition-colors duration-150"><Trash2 className="w-4 h-4" /></button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <BorcluGorusmeleri items={rec.gorusmeler || []} canAdd={canHaciz} isAdmin={isAdmin} onAdd={addGorusme} onDelete={delGorusme} />

      {showPromise && (
        <OdemeSozuDialog title="İcra Ödeme Sözü / Anlaşma" onClose={() => setShowPromise(false)} onSubmit={addPromise} />
      )}
      {editSoz && (
        <OdemeSozuDialog title="Ödeme Sözü Düzenle" initial={editSoz} submitLabel="Güncelle" onClose={() => setEditSoz(null)} onSubmit={saveEditPromise} />
      )}

      {rec.senet && (
        <div className="bg-white border border-[#2F6E4E]/40 rounded-sm p-4 mt-6" data-testid="icra-senet-card">
          <div className="kicker mb-2 flex items-center gap-1.5 text-[#2F6E4E]"><FileText className="w-3.5 h-3.5" /> SÖZLEŞME SENEDİ</div>
          {(rec.senet.vade_tarihi || rec.senet.tutar) ? (
            <div className="flex flex-wrap gap-4 mb-2 text-sm" data-testid="icra-senet-bilgi">
              {rec.senet.vade_tarihi && <span className="text-[#8C8377]">Senet Vadesi: <b className="text-[#26231E] tabular-nums">{fmtDate(rec.senet.vade_tarihi)}</b></span>}
              {rec.senet.tutar ? <span className="text-[#8C8377]">Senet Tutarı (Toplam Borç): <b className="text-[#26231E] tabular-nums">{fmt(rec.senet.tutar)}</b></span> : null}
            </div>
          ) : null}
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/records/${rec.id}/senet/view`} target="_blank" rel="noopener noreferrer" data-testid="icra-senet-link">
            <img src={`${process.env.REACT_APP_BACKEND_URL}/api/records/${rec.id}/senet/view`} alt="Senet" className="max-h-64 w-auto rounded-sm border border-[#E7DECC] hover:opacity-90 transition-opacity duration-150" />
          </a>
          <p className="text-xs text-[#8C8377] mt-1.5">Cari'den aktarıldı. Büyütmek için tıklayın.</p>
        </div>
      )}

      <div className="bg-white border border-[#E7DECC] rounded-sm p-4 mt-6" data-testid="icra-belgeler">
        <div className="kicker mb-3 flex items-center gap-1.5 text-[#B8933F]"><FileDown className="w-3.5 h-3.5" /> BELGE OLUŞTUR</div>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => indirBlob("ih_once", `/records/${rec.id}/belge/ih_once`)} disabled={belgeBusy === "ih_once"} data-testid="belge-ih-once" className="flex items-center gap-1.5 px-4 py-2 bg-[#B8933F] hover:bg-[#9C7A33] text-white rounded-sm text-sm font-medium transition-colors duration-150 disabled:opacity-60">
            <FileDown className="w-4 h-4" /> {belgeBusy === "ih_once" ? "..." : "İhtiyati Haciz — ÖNCE (.udf)"}
          </button>
          <button onClick={() => (rec.dosya_no ? indirBlob("ih_sonra", `/records/${rec.id}/belge/ih_sonra`) : toast.error("Takipten SONRA için İcra Dairesi ve Dosya No girin"))} disabled={belgeBusy === "ih_sonra"} data-testid="belge-ih-sonra" className={`flex items-center gap-1.5 px-4 py-2 rounded-sm text-sm font-medium transition-colors duration-150 disabled:opacity-60 ${rec.dosya_no ? "bg-[#B8933F] hover:bg-[#9C7A33] text-white" : "bg-[#EFE7D6] text-[#B3A88F] cursor-not-allowed"}`}>
            <FileDown className="w-4 h-4" /> {belgeBusy === "ih_sonra" ? "..." : "İhtiyati Haciz — SONRA (.udf)"}
          </button>
          <button onClick={() => setShowTeminat(true)} data-testid="belge-teminat" className="flex items-center gap-1.5 px-4 py-2 bg-[#26231E] hover:bg-[#333] text-white rounded-sm text-sm font-medium transition-colors duration-150">
            <FileDown className="w-4 h-4" /> Teminat Mektubu (.docx)
          </button>
        </div>
        <p className="text-xs text-[#8C8377] mt-2">Föydeki borçlu, tutar (senet/asıl alacak) ve vade bilgileriyle otomatik doldurulur. İhtiyati haciz dilekçeleri UYAP'a yüklenebilir .udf; teminat mektubu .docx olarak iner. "SONRA" için dosya no gerekir.</p>
      </div>
      {showTeminat && <BelgeTeminatModal rec={rec} busy={belgeBusy === "teminat"} onClose={() => setShowTeminat(false)} onSubmit={(f) => indirBlob("teminat", `/records/${rec.id}/teminat-belge`, f)} />}

      <TakipXmlSection rec={rec} canEdit={canHaciz} indirBlob={indirBlob} busy={belgeBusy} onSaved={load} />

      <BorclularSection rec={rec} canEdit={canHaciz} onChanged={setRec} />

      <HacizEvrakSection rec={rec} canHaciz={canHaciz} onChanged={setRec} />
    </Layout>
  );
}

function Stat({ label, value, color, testId }) {
  return (
    <div className="bg-white border border-[#E7DECC] rounded-sm p-4" data-testid={testId}>
      <div className="kicker mb-1.5">{label}</div>
      <div className="font-heading font-bold text-lg tabular-nums" style={color ? { color } : undefined}>{value}</div>
    </div>
  );
}
